import React, { useState } from 'react';
import { 
    Show, 
    SimpleShowLayout, 
    TextField, 
    DateField, 
    TopToolbar, 
    Button, 
    useRecordContext, 
    useDataProvider, 
    useNotify, 
    useRefresh 
} from 'react-admin';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { cms1500JobsFields } from '../cms1500Jobs.model';
import DefaultPlaceholderField from '../../material/defPlaceholderField.component';
import Cms1500JobTitle from './fields/cms1500JobTitle.component';
import { DATE_TIME_OPTIONS } from '../../app/app.component';
import PatientLinkField from "./fields/patientLinkField.component";
import HealthieCms1500LinkField from "./fields/healthieCms1500LinkField.component";
import RetryInitiatedAtField from "./fields/retryInitiatedAtField.component";
import { POST_FORCE_RETRY } from '../cms1500Jobs.dataProvider';
import logger from '../../utils/logger';
const Cms1500JobShowActions = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!record) return null;

    const canRetry = record.status === 'FAILED_TERMINAL';

    const handleForceRetry = async () => {
        setLoading(true);
        try {
            const result = await dataProvider.forceRetry('cms1500-jobs', { 
                id: record.id,
                form_answer_group_id: record.form_answer_group_id 
            });
            
            notify('Force retry initiated successfully', { type: 'success' });
            refresh();
            setOpen(false);
        } catch (error) {
            logger.error('[ForceRetryButton] Force retry failed:', error);
            notify('Force retry failed. Please try again.', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <TopToolbar>
            <Button
                label="Force Retry"
                onClick={() => setOpen(true)}
                startIcon={<PlayArrowIcon />}
                disabled={!canRetry}
            />
            
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Confirm Force Retry</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to force retry this CMS-1500 generation job?
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                        <Typography variant="body2"><strong>Job ID:</strong> {record.id}</Typography>
                        <Typography variant="body2"><strong>Chart Note ID:</strong> {record.form_answer_group_id}</Typography>
                        <Typography variant="body2"><strong>Current Status:</strong> {record.status}</Typography>
                        <Typography variant="body2"><strong>Attempt Count:</strong> {record.attempt_count}</Typography>
                        {record.last_error_message && (
                            <Typography variant="body2"><strong>Last Error:</strong> {record.last_error_message}</Typography>
                        )}
                    </Box>
                    <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
                        This will immediately trigger a retry regardless of current status or attempt count.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleForceRetry} 
                        variant="contained" 
                        color="warning"
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : 'Retry'}
                    </Button>
                </DialogActions>
            </Dialog>
        </TopToolbar>
    );
};

const Cms1500JobShow = () => {
    return (
        <Show title={<Cms1500JobTitle />} actions={<Cms1500JobShowActions />}>
            <SimpleShowLayout>
                <DefaultPlaceholderField 
                    source={cms1500JobsFields.id} 
                    wrapField={<TextField/>} 
                    label="ID" 
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.formAnswerGroupId}
                    label="Chart Note ID"
                    wrapField={<TextField source={cms1500JobsFields.formAnswerGroupId} />}
                />
                <PatientLinkField 
                    source={cms1500JobsFields.patientId} 
                    label="Patient ID" 
                    addLabel 
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.appointmentId}
                    label="Appointment ID"
                    wrapField={<TextField source={cms1500JobsFields.appointmentId} />}
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.status}
                    label="Status"
                    wrapField={<TextField source={cms1500JobsFields.status} />}
                />
                <HealthieCms1500LinkField 
                    source={cms1500JobsFields.healthieCms1500Id} 
                    label="Healthie CMS1500 ID" 
                    addLabel 
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.attemptCount}
                    label="Attempt Count"
                    wrapField={<TextField source={cms1500JobsFields.attemptCount} />}
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.lastErrorMessage}
                    label="Last Error Message"
                    wrapField={<TextField source={cms1500JobsFields.lastErrorMessage} />}
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.lastAttemptedAt}
                    options={DATE_TIME_OPTIONS}
                    wrapField={<DateField/>}
                    label="Last Attempted At"
                />
                <RetryInitiatedAtField
                    source={cms1500JobsFields.retryInitiatedAt}
                    label="Last Retry Initiated"
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.createdAt}
                    options={DATE_TIME_OPTIONS}
                    wrapField={<DateField/>}
                    label="Created At"
                />
                <DefaultPlaceholderField
                    source={cms1500JobsFields.updatedAt}
                    options={DATE_TIME_OPTIONS}
                    wrapField={<DateField/>}
                    label="Updated At"
                />
            </SimpleShowLayout>
        </Show>
    );
};

export default Cms1500JobShow;
